<template>
  <UIPanel
    v-model="clearedSearch"
    v-model:storage="searchStorage"
    :disabled
    placeholder="Search linked transactions..."
  >
    <template #default="{ size, ButtonExport, ButtonGroup }">
      <UIFilter
        v-if="!hideFilter"
        v-model="clearedFilter"
        v-model:storage="filterStorage"
        :disabled
        :size
        :fields="filterFields"
      />
      <UISort
        v-model="clearedSort"
        v-model:storage="sortStorage"
        :disabled
        :size
        :fields="sortFields"
      />
      <UIDropdownActions
        v-if="selectedIds.length"
        :data="groupActions"
      >
        <template #default="{ toggle }">
          <component
            :is="ButtonGroup"
            :disabled
            @click="toggle"
          />
        </template>
      </UIDropdownActions>
      <component
        :is="ButtonExport"
        :disabled
        @click="handleExport"
      />
    </template>
  </UIPanel>
  <UIGridSkeleton
    v-if="isLoading"
    :columns
    :sm
    wrapped
    selectable
    message="Loading transactions..."
  />
  <UIGrid
    v-else
    ref="gridRef"
    v-model:sort="sortStorage"
    v-model:selected="selectedIds"
    :columns
    :items
    :readonly
    :sm
    :value-paster
    data-refid="linkedTransactionsList"
    wrapped
    selectable
    scrollable
    editable
    @pasted="handlePasted"
  >
    <template #actions="{ item, size }">
      <UIButton
        v-if="!checkTransactionHasExcludeTag(item)"
        label="Exclude"
        variant="danger"
        fill="light"
        :size
        @click="handleExclude(item)"
      />
      <UIButton
        v-if="!readonly"
        :label="isMobile ? undefined : 'Create tagging rule'"
        :leading="TagIcon"
        variant="secondary"
        fill="light"
        :size
        @click="handleCreateTaggingRule(item)"
      />
      <UIButton
        v-if="!readonly"
        :label="isMobile ? undefined : 'Create mapping rule'"
        :leading="FunnelIcon"
        variant="secondary"
        fill="light"
        :size
        @click="handleCreateMappingRule(item)"
      />
      <UIButton
        label="Source view"
        variant="secondary"
        fill="light"
        :size
        :leading="ArrowTopRightOnSquareIcon"
        hide-label-to-sm
        @click="handleOpenJSON(item)"
      />
    </template>
    <template #cell="{ columnName, editCell, item }">
      <UIInputText
        v-if="editCell"
        :model-value="item[columnName]"
        size="sm"
        @mouseup.stop
        @mousedown.stop
        @ready="(e: HTMLInputElement) => e.focus()"
        @click:enter="handleEditClickEnter"
        @click:tab="handleEditClickTab"
        @click:escape="handleEditReset"
        @update:model-value="
          handleUpdateModelValue(item.id, columnName as string, $event)
        "
      />
    </template>
    <template #cellAmount="{ columnName, displayValue, editCell, item }">
      <UIInputNumber
        v-if="editCell"
        :model-value="item[columnName]"
        size="sm"
        @mouseup.stop
        @mousedown.stop
        @ready="(e: HTMLInputElement) => e.focus()"
        @click:enter="handleEditClickEnter"
        @click:tab="handleEditClickTab"
        @click:escape="handleEditReset"
        @update:model-value="
          handleUpdateModelValue(item.id, columnName as string, $event)
        "
      />
      <span
        v-else
        :class="getAmountCellClass(displayValue)"
      >
        {{ displayValue }}
      </span>
    </template>
    <template #cellOverrideDate="{ columnName, displayValue, editCell, item }">
      <UIInputDate
        v-if="editCell"
        :model-value="item[columnName]"
        size="sm"
        focus-on-load
        disable-focus-delay
        disable-recovery-value
        @mouseup.stop
        @mousedown.stop
        @select="handleSelectDate(item.id, columnName as string, $event)"
        @click:enter="handleEditClickEnter"
        @click:tab="handleEditClickTab"
        @reset="handleEditReset"
        @update:model-value="
          handleUpdateModelValue(item.id, columnName as string, $event as string)
        "
      />
      <template v-else>
        {{ displayValue }}
      </template>
    </template>
    <template #cellTags="{ displayValue: tags, item }">
      <ListTags
        :id="item.id"
        ref="listTagsRef"
        :is-store-used
        :tags
        @click:tag="handleClickTag"
        @click:assign="handleClickAssign"
        @update="handleUpdate"
        @rename-tag="onTagsUpdate"
      />
    </template>
  </UIGrid>
  <UIPagination
    v-if="!isLoading"
    :disabled
    :page-no
    :page-size="pagination?.page_size"
    :total="pagination?.total"
    @click:page="handleClickPage"
    @update:page-size="handleUpdatePageSize"
  />
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  markRaw,
  nextTick,
  onBeforeMount,
  onWatcherCleanup,
  ref,
  useTemplateRef,
  watch,
} from 'vue'
import { isEqual } from 'lodash'
import { debouncedWatch, watchPausable } from '@vueuse/core'

import {
  Filter,
  FilterComparison,
  FilterLogic,
  LinkedDataTransaction,
  LinkedDataTransactionUpdate,
  PaginatedMeta,
  ReadonlyMode,
  Sort,
  TransactionRule,
  TransactionRuleCriteriaType,
} from '@types'
import { PresetFilter } from '../utils/types'
import { TaggingRuleAction } from '../../TaggingRules/utils/types'

import { LINKED_TRANSACTION_FIELDS } from '../utils/const'
import { DEBOUNCE_DELAY, NOTIFICATION_DELAY, READONLY_MODE } from '@/const/common'

import { numberFormat, numberParse } from '@/helpers/numbers'
import { parseDate } from '@/helpers/dates'
import { downloadAsFile, handleCatchedError } from '@/helpers/common'
import { getLinkedDataTransactionsFilters } from '../utils/helpers'
import { updateStoreListItem } from '@/store/utils/helpers'

import { useResponsive } from '@/plugins/responsiveUI'
import { useNotifications } from '@/plugins/notification'

import { useLinkedDataTaggingRulesStore } from '@/store/linkedData/rules/tagging'
import { useLinkedDataMappingRulesStore } from '@/store/linkedData/rules/mapping'
import { useLinkedDataTransactionsStore } from '@/store/linkedData/transactions'
import { useLinkedDataTransactionsTagsStore } from '@/store/linkedData/transactionsTags'
import { useModalsStore } from '@/store/modals'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import {
  ArrowTopRightOnSquareIcon,
  FunnelIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { UIPagination, UIPanel } from '@ui/core'
import { UIFilter, UISort } from '@ui/controllers'
import { UIDropdownActions } from '@ui/dropdowns'
import { UIInputDate, UIInputNumber, UIInputText } from '@ui/fields'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import { UIDialogRemove } from '@ui/modals'
import ListItemJson from './ListItemJson.vue'
import ListTags from './ListTags.vue'
import TaggingRulesForm from '../../TaggingRules/TaggingRulesForm.vue'
import MappingRulesForm from '../../MappingRules/MappingRulesForm.vue'
import ListTagAssign from './ListTagAssign.vue'
import ListTagRemove from './ListTagRemove.vue'

type Props = {
  presetFilter?: PresetFilter
  assetId?: string
  excludedColumns?: string[]
  hideFilter?: boolean
}

const props = defineProps<Props>()

const searchStorage = defineModel<string>('search')
const filterStorage = defineModel<Filter>('filter')
const sortStorage = defineModel<Sort[]>('sort')
const pageSizeStorage = defineModel<number>('pageSize')

const linkedDataTaggingRulesStore = useLinkedDataTaggingRulesStore()
const linkedDataMappingRulesStore = useLinkedDataMappingRulesStore()
const linkedDataTransactionsStore = useLinkedDataTransactionsStore()
const linkedDataTransactionsTagsStore = useLinkedDataTransactionsTagsStore()
const modalsStore = useModalsStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const filterFields = getLinkedDataTransactionsFilters()

const { isMobile } = useResponsive()
const { progress, remove, update, warn } = useNotifications()

const gridRef = useTemplateRef('gridRef')
const listTagsRef = useTemplateRef('listTagsRef')

const internalItems = ref<LinkedDataTransaction[]>()
const internalPagination = ref<PaginatedMeta>()
const internalLoading = ref(true)

const clearedSearch = ref<string>()
const clearedFilter = ref<Filter>()
const clearedSort = ref<Sort[]>()

const pageNo = ref(0)

const selectedIds = ref<string[]>([])

const currentSaveModel = ref<{
  id: string
  field: string
  value: string | number | null
}>()

const bulkUpdateData = ref<Map<string, LinkedDataTransactionUpdate>>(new Map())

const readonly = inject<ReadonlyMode>(READONLY_MODE)

const isStoreUsed = computed(() => !props.presetFilter && !props.assetId)

const mergedFilter = computed(() => {
  if (props.presetFilter) {
    const params = props.presetFilter
    if (clearedFilter.value) {
      params.push(clearedFilter.value)
    }
    return {
      logic: FilterLogic.AND,
      params,
    }
  }

  return clearedFilter.value
})

const isInit = computed(
  () =>
    !isStoreUsed.value ||
    (isStoreUsed.value && linkedDataTransactionsStore.initFlag),
)
const isLoading = computed(
  () =>
    (!isStoreUsed.value && internalLoading.value) ||
    (isStoreUsed.value && linkedDataTransactionsStore.loading),
)

const disabled = computed(() => !isInit.value)

const pagination = computed(
  () =>
    (!isStoreUsed.value && internalPagination.value) ||
    linkedDataTransactionsStore.getMeta,
)

const groupActions = computed(() => [
  {
    label: 'Add tags',
    icon: TagIcon,
    action: (hide: () => void) => {
      hide()
      handleAssign(selectedIds.value)
    },
  },
  {
    label: 'Remove tags',
    icon: XMarkIcon,
    action: (hide: () => void) => {
      hide()
      handleRemove(selectedIds.value)
    },
  },
])

const sm = '1fr 1fr'
const initialColumns = computed(() => [
  {
    name: LINKED_TRANSACTION_FIELDS.CONNECTOR_NAME.field,
    caption: LINKED_TRANSACTION_FIELDS.CONNECTOR_NAME.label,
    uneditable: true,
    cellClasses: 'ui-grid__cell--span-2',
  },
  {
    name: LINKED_TRANSACTION_FIELDS.ACCESS_METHOD.field,
    caption: LINKED_TRANSACTION_FIELDS.ACCESS_METHOD.label,
    default: '.75fr',
    uneditable: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.ACCOUNT_NAME.field,
    caption: LINKED_TRANSACTION_FIELDS.ACCOUNT_NAME.label,
    cellValueClasses: 'asset-node',
    default: '2fr',
    uneditable: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.ACCOUNT_NUMBER.field,
    caption: LINKED_TRANSACTION_FIELDS.ACCOUNT_NUMBER.label,
    uneditable: true,
    cellClasses: 'ui-grid__cell--span-2',
  },
  {
    name: LINKED_TRANSACTION_FIELDS.ACCOUNT_TYPE.field,
    caption: LINKED_TRANSACTION_FIELDS.ACCOUNT_TYPE.label,
    uneditable: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.DATE.field,
    caption: LINKED_TRANSACTION_FIELDS.DATE.label,
    uneditable: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.OVERRIDE_DATE.field,
    caption: LINKED_TRANSACTION_FIELDS.OVERRIDE_DATE.label,
    default: '.85fr',
  },
  {
    name: LINKED_TRANSACTION_FIELDS.YEAR.field,
    caption: LINKED_TRANSACTION_FIELDS.YEAR.label,
    uneditable: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.QUARTER.field,
    caption: LINKED_TRANSACTION_FIELDS.QUARTER.label,
    uneditable: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.MONTH.field,
    caption: LINKED_TRANSACTION_FIELDS.MONTH.label,
    uneditable: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.DESCRIPTION.field,
    caption: LINKED_TRANSACTION_FIELDS.DESCRIPTION.label,
    lineClamp: 3,
    cellClasses: 'ui-grid__cell--span-2',
    default: '2fr',
    hideCaptionIfCollapsed: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.TYPE.field,
    caption: LINKED_TRANSACTION_FIELDS.TYPE.label,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.AMOUNT.field,
    caption: LINKED_TRANSACTION_FIELDS.AMOUNT.label,
    formatter: numberFormat,
    headerValueClasses: 'linked-transactions__rtl',
    cellValueClasses: 'linked-transactions__rtl number-node',
  },
  {
    name: LINKED_TRANSACTION_FIELDS.TAGS.field,
    caption: LINKED_TRANSACTION_FIELDS.TAGS.label,
    unsortable: true,
    uneditable: true,
    tooltip: null,
    cellClasses: 'ui-grid__cell--span-2 linked-transactions__tags',
    cellValueClasses: 'linked-transactions__tags-value',
    hideCaptionIfCollapsed: true,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.CATEGORY.field,
    caption: LINKED_TRANSACTION_FIELDS.CATEGORY.label,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.SUBCATEGORY.field,
    caption: LINKED_TRANSACTION_FIELDS.SUBCATEGORY.label,
  },
  {
    name: LINKED_TRANSACTION_FIELDS.COUNTERPARTY.field,
    caption: LINKED_TRANSACTION_FIELDS.COUNTERPARTY.label,
    lineClamp: 3,
    default: '2fr',
  },
  {
    name: LINKED_TRANSACTION_FIELDS.STATUS.field,
    caption: LINKED_TRANSACTION_FIELDS.STATUS.label,
  },
])
const columns = computed(() =>
  initialColumns.value.filter(
    column => !props.excludedColumns?.includes(column.name),
  ),
)

const items = computed(
  () =>
    (!isStoreUsed.value && internalItems.value) ||
    linkedDataTransactionsStore.getList ||
    [],
)

const sortFields = computed(() =>
  columns.value
    .filter(column => !column.unsortable)
    .map(column => ({
      value: column.name,
      label: column.caption,
    })),
)

const tagsList = computed(() => linkedDataTransactionsTagsStore.getList)
const excludeTag = computed(() =>
  tagsList.value.find(tag => tag.name === 'exclude'),
)

const getAmountCellClass = (value: string) => ({
  'linked-transactions__amount--positive': !value.startsWith('-'),
  'linked-transactions__amount--negative': value.startsWith('-'),
})

const handleClickTag = (value: string) => {
  searchStorage.value = value
}

const handleClickAssign = (id: string) => {
  handleAssign([id])
  onTagsUpdate()
}

const handleAssign = (ids: string[]) => {
  const modalInstance = modalsStore.init(
    'list-tag-assign',
    markRaw(ListTagAssign),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    items,
    ids,
    onProcess: handleProcessTags,
  })
}

const handleRemove = (ids: string[]) => {
  const modalInstance = modalsStore.init(
    'list-tag-remove',
    markRaw(ListTagRemove),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    items,
    ids,
    onProcess: handleProcessTags,
  })
}


const handleUpdate = (data: LinkedDataTransaction[]) => {
  if (isStoreUsed.value) return
  data.forEach(value => {
    if (!internalItems.value) return
    updateStoreListItem(internalItems.value, value)
  })
  onTagsUpdate()
}

const handleExclude = (transaction: LinkedDataTransaction) => {
  listTagsRef.value?.exclude(transaction)
}

const checkTransactionHasExcludeTag = (transaction: LinkedDataTransaction) => {
  return (
    readonly?.value ||
    transaction.tags.map(t => t.id).includes(excludeTag.value?.id)
  )
}

const getData = (transaction: LinkedDataTransaction) => {
  const criteria: TransactionRule<TaggingRuleAction[]>['data']['criteria'] = []
  const actions: TransactionRule<TaggingRuleAction[]>['data']['actions'] = []
  if (transaction.description) {
    criteria.push({
      type: TransactionRuleCriteriaType.DESCRIPTION,
      logic: FilterLogic.AND,
      params: [
        {
          comparison: FilterComparison.CONTAINS,
          value: transaction.description,
        },
      ],
    })
  }
  if (transaction.account_name) {
    criteria.push({
      type: TransactionRuleCriteriaType.ACCOUNT_NAME,
      logic: FilterLogic.AND,
      params: [
        {
          comparison: FilterComparison.EQ,
          value: transaction.account_name,
        },
      ],
    })
  }
  if (transaction.account_number) {
    criteria.push({
      type: TransactionRuleCriteriaType.ACCOUNT_NUMBER,
      logic: FilterLogic.AND,
      params: [
        {
          comparison: FilterComparison.EQ,
          value: transaction.account_number,
        },
      ],
    })
  }
  if (transaction.amount) {
    criteria.push({
      type: TransactionRuleCriteriaType.AMOUNT,
      logic: FilterLogic.AND,
      params: [
        {
          comparison: FilterComparison.EQ,
          value: transaction.amount,
        },
      ],
    })
  }
  if (transaction.type) {
    criteria.push({
      type: TransactionRuleCriteriaType.TRANSACTION_TYPE,
      logic: FilterLogic.AND,
      params: [
        {
          comparison: FilterComparison.EQ,
          value: transaction.type,
        },
      ],
    })
  }
  if (transaction.category) {
    criteria.push({
      type: TransactionRuleCriteriaType.CATEGORY,
      logic: FilterLogic.AND,
      params: [
        {
          comparison: FilterComparison.EQ,
          value: transaction.category,
        },
      ],
    })
  }
  if (transaction.account_type) {
    criteria.push({
      type: TransactionRuleCriteriaType.ACCOUNT_TYPE,
      logic: FilterLogic.AND,
      params: [
        {
          comparison: FilterComparison.EQ,
          value: transaction.account_type,
        },
      ],
    })
  }

  return {
    criteria,
    actions,
  }
}

const handleCreateTaggingRule = async (transaction: LinkedDataTransaction) => {
  const order = linkedDataTaggingRulesStore.getList.at(-1)?.order || 0
  const modalInstance = modalsStore.init(
    'list-tagging-rules-create',
    markRaw(TaggingRulesForm),
  )
  const data = getData(transaction)
  modalInstance?.open(modalsStore.getZIndex(), {
    prefilled: true,
    rule: {
      order,
      data,
    },
    onFetch: () => {
      fetch()
      linkedDataTaggingRulesStore.initFlag = false
    },
  })
}

const handleCreateMappingRule = async (transaction: LinkedDataTransaction) => {
  try {
    const jsonData = await linkedDataTransactionsStore.getJSON(transaction.id)
    const order = 1
    const modalInstance = modalsStore.init('list-maping-rules-create', markRaw(MappingRulesForm))
    const data = getData(transaction)
    if (transaction.connector_id) {
      // Connector scoping is an id-based criterion (value is the stable connector id).
      data.criteria.push({
        type: TransactionRuleCriteriaType.CONNECTOR,
        logic: FilterLogic.AND,
        params: [
          {
            comparison: FilterComparison.EQ,
            value: transaction.connector_id,
          },
        ],
      })
    }
    if (transaction.access_method) {
      data.criteria.push({
        type: TransactionRuleCriteriaType.ACCESS_METHOD,
        logic: FilterLogic.AND,
        params: [
          { comparison: FilterComparison.EQ, value: transaction.access_method },
        ],
      })
    }
    if (transaction.status) {
      data.criteria.push({
        type: TransactionRuleCriteriaType.STATUS,
        logic: FilterLogic.AND,
        params: [
          {
            comparison: FilterComparison.EQ,
            value: transaction.status,
          },
        ],
      })
    }
    modalInstance?.open(modalsStore.getZIndex(), {
      prefilled: true,
      rule: {
        order,
        data,
      },
      jsonData,
      onFetch: () => {
        linkedDataMappingRulesStore.initFlag = false
      }
    })
  } catch (e) {
    handleCatchedError(e as string, { transactionId: transaction.id })
  }
}

const handleOpenJSON = async (transaction: LinkedDataTransaction) => {
  try {
    const json = await linkedDataTransactionsStore.getJSON(transaction.id)
    const modalInstance = modalsStore.init(
      `Linked Transaction JSON [${transaction.id}]`,
      markRaw(ListItemJson),
      { allowTray: true }
    )
    modalInstance?.open(modalsStore.getZIndex(), {
      json,
    })
  } catch (e) {
    handleCatchedError(e as string, { transactionId: transaction.id })
  }
}

const handleClickPage = (page: number) => {
  pageNo.value = page
}

const handleUpdatePageSize = (newPageSize: number) => {
  if (!pageSizeStorage.value) return
  pageSizeStorage.value = newPageSize
  pageNo.value = 0
}

const handleExport = async () => {
  const nid = await progress({
    message: 'Exporting transactions',
  })
  try {
    const result = await linkedDataTransactionsStore.export({
      search: clearedSearch.value,
      filter: mergedFilter.value as Filter,
      sort: clearedSort.value,
    })
    downloadAsFile(result, 'linked-data-transactions', 'csv')
    await update(
      nid,
      {
        type: 'success',
        message: 'Transactions exported',
      },
      NOTIFICATION_DELAY,
    )
  } catch (e) {
    handleCatchedError(e as string)
    await update(nid, {
      type: 'error',
      message: 'Failed to export transactions',
    })
  }
}

const handleRefresh = () => {
  fetch()
  linkedDataTaggingRulesStore.fetch()
}

const onTagsUpdate = () => {
  const filterIncludesTags = (mergedFilter.value as Filter)?.params.some(
    filter => filter.params?.some(param => param.field === 'tags'),
  )

  if (filterIncludesTags) {
    handleRefresh()
  }
}

const fetch = async (signal?: AbortSignal) => {
  selectedIds.value = []
  internalLoading.value = true
  const options = { asset_id: props.assetId, page_no: pageNo.value }

  linkedDataTransactionsStore.setOptions({
    search: clearedSearch.value,
    filter: mergedFilter.value as Filter,
    sort: clearedSort.value,
  })
  try {
    const result = await linkedDataTransactionsStore.fetch(
      options,
      isStoreUsed.value,
      signal,
      true,
      pageSizeStorage.value,
    )
    if (isStoreUsed.value) return
    internalItems.value = result.data
    internalPagination.value = result.meta
    internalLoading.value = false
  } catch (e) {
    handleCatchedError(e as string, options)
  }
}

const handleEditClickEnter = async (event: Event) => {
  event.stopPropagation()
  await updateTransaction()
  gridRef.value?.release()
  gridRef.value?.clickEnter()
}

const handleEditClickTab = async (event: Event) => {
  event.stopPropagation()
  event.preventDefault()
  await updateTransaction()
  gridRef.value?.release()
  gridRef.value?.clickTab()
}

const handleEditReset = () => {
  gridRef.value?.release()
}

const handleUpdateModelValue = (
  id: string,
  field: string,
  value: string | number | null,
) => {
  currentSaveModel.value = { id, field, value }
}

const handleSelectDate = (id: string, field: string, value: string | null) => {
  handleUpdateModelValue(id, field, value)
  updateTransaction()
  gridRef.value?.release()
  gridRef.value?.clickEnter()
}

const updateTransaction = async () => {
  if (!currentSaveModel.value) return
  const { id, field, value } = currentSaveModel.value
  const transaction = items.value.find(item => item.id === id)
  if (
    !transaction ||
    transaction[field as keyof LinkedDataTransaction] === value
  )
    return
  currentSaveModel.value = undefined
  const result = await linkedDataTransactionsStore.bulkUpdate([
    { id: transaction.id, [field]: value },
  ])
  if (!internalItems.value || !result[0]) return
  updateStoreListItem(internalItems.value, result[0])
}

const valuePaster = (
  field: string,
  item: any,
  value: number | string,
  check?: boolean,
) => {
  if (columns.value.find(column => column.name === field)?.uneditable) return
  if (check) {
    switch (field) {
      case 'date': {
        const { isoDate, isAmbiguous } = parseDate(value.toString())

        if (isAmbiguous) {
          warn({
            message: `The pasted date "${value}" is ambiguous and could not be processed.`,
          })
          return
        }
        if (!isoDate) return

        value = isoDate
        break
      }
      case 'amount': {
        const amount = numberParse(value)
        value = amount
        break
      }
    }
  }
  const key = field as keyof LinkedDataTransactionUpdate
  const data = bulkUpdateData.value.get(item.id)
  if (data) {
    bulkUpdateData.value.set(item.id, {
      ...data,
      [key]: value,
    })
  } else {
    bulkUpdateData.value.set(item.id, { id: item.id, [key]: value })
  }
}

const handlePasted = () => {
  if (!bulkUpdateData.value.size) return
  const modalInstance = modalsStore.init(
    'linked-data-transactions-update-selected-columns',
    markRaw(UIDialogRemove),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    title: 'Update selected columns',
    message: 'Are you sure you want to update selected fields?',
    label: 'Update',
    onRemove: handleBulkUpdate,
  })
}

const handleBulkUpdate = async () => {
  if (!bulkUpdateData.value.size) return
  const data = Array.from(bulkUpdateData.value.values())
  bulkUpdateData.value = new Map()
  await processTransactions(data)
  gridRef.value?.deselect()
}

const processTransactions = async (
  data: LinkedDataTransactionUpdate[],
  callback?: () => void,
) => {
  const nid = await progress({
    message: 'Process transactions',
  })
  try {
    const result = await linkedDataTransactionsStore.bulkUpdate(data)
    if (nid) {
      await update(
        nid,
        {
          type: 'success',
          message: 'Transactions processed',
        },
        NOTIFICATION_DELAY,
      )
    }
    if (!isStoreUsed.value) {
      result.forEach(value => {
        if (!internalItems.value) return
        updateStoreListItem(internalItems.value, value)
      })
    }
  } catch (e) {
    if (nid) await remove(nid)
    handleCatchedError(e as string)
  } finally {
    callback?.()
  }
}

const handleProcessTags = async (
  data: LinkedDataTransactionUpdate[],
  callback: () => void,
) => {
  await processTransactions(data, callback)
  onTagsUpdate()
}

const { pause, resume } = watchPausable(pageNo, () => {
  const controller = new AbortController()
  fetch(controller.signal)
  onWatcherCleanup(() => {
    controller.abort()
  })
})

watch(pageSizeStorage, () => {
  const controller = new AbortController()
  fetch(controller.signal)
  onWatcherCleanup(() => {
    controller.abort()
  })
})

debouncedWatch(
  () => ({
    search: clearedSearch.value,
    filter: mergedFilter.value,
    sort: clearedSort.value,
    asset_id: props.assetId,
  }),
  (value, prev) => {
    if (isEqual(value, prev)) return
    pause()
    pageNo.value = 0
    const controller = new AbortController()
    fetch(controller.signal)
    onWatcherCleanup(() => {
      controller.abort()
    })
    nextTick(resume)
  },
  {
    deep: true,
    immediate: true,
    debounce: DEBOUNCE_DELAY,
  },
)

onBeforeMount(() => {
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
})
</script>

<style>
.linked-transactions {
  &__rtl {
    .ui-grid:not(.ui-grid--collapsed) & {
      @apply sm:text-right;
    }
  }

  &__tags {
    @apply min-h-auto!;

    &-value {
      @apply py-0.5;
      @apply overflow-visible!;
    }
  }

  &__amount--positive {
    @apply text-gray-950 dark:text-green-400;
  }
  &__amount--negative {
    @apply text-red-600 dark:text-red-400;
  }
}
.group-actions {
  &__item {
    @apply w-36;
    @apply flex items-center;
    @apply px-4 py-2;
    @apply text-sm sm:text-xs;
  }

  &__icon {
    @apply w-4 h-4;
    @apply mr-1.5 -ml-1;
    @apply text-gray-400;
  }
}
</style>
